import { lessonCompletionSchema, type LessonCompletion } from '@orborix/shared-types';
import { useEffect, useMemo, useRef, useState } from 'react';
import { splitObjectives, type ScenarioMission } from '../../scenarios/scenarioTypes.js';
import { sfx } from '../../../lib/sfx/index.js';

/** Sub-unidad activa del Programa en el cliente (fase 17.B). */
export interface ActiveSubUnitClient {
  unitId: string;
  subUnitId: string;
  order: number;
  total: number;
  titleEs: string;
  titleEn: string;
  /** De qué trata, en español (línea de intro del chat). `null` = el server no lo mandó. */
  blurbEs: string | null;
  /** `label` = what the tracker shows (es): a can-do lesson's skill label;
   *  `null` on an old sub-unit, whose tracker shows its criterion. */
  objectives: { id: string; criterion: string; label: string | null; reps: number }[];
  /**
   * `true` = REPASO: la sub-unidad ya está aprobada y el alumno volvió a
   * practicarla, así que los anillos arrancan en 0 con los `reps` de hoy. El
   * sello no se toca (el panel del Programa la sigue mostrando «Completada»).
   */
  review: boolean;
}

/** Shape del `detail` del window event `orborix:subunit.advanced`. */
interface SubUnitAdvancedDetail {
  unitId: string;
  subUnitId: string;
  order: number;
  total: number;
  titleEs: string;
  titleEn: string;
  blurbEs?: string;
  objectives: Array<{
    id: string;
    criterion: string;
    label?: { es: string; en: string };
    reps: number;
    count?: number;
  }>;
  isFinal: boolean;
  review?: boolean;
  completesUnit?: boolean;
  initial: boolean;
}

export interface UseObjectiveTrackersReturn {
  lessonCompletion: LessonCompletion | null;
  // Misión de escenario (B6)
  completedObjectives: ReadonlyArray<string>;
  justObjectiveId: string | null;
  allObjectivesDone: boolean;
  /**
   * Charla asistida: objetivos PRACTICADOS con la respuesta modelo. No cumplen
   * la misión (Emmanuel, 13/10), pero el tracker los muestra aparte (14/10).
   */
  practicedObjectives: ReadonlyArray<string>;
  justPracticedId: string | null;
  // Unit del Programa (fase 16.B)
  /**
   * Escenario con los primarios cumplidos y el bonus activo SIN cumplir: su
   * etiqueta, para el recuadro «hacé esto para el bonus» (la charla sigue).
   * `null` en cualquier otro caso.
   */
  bonusPending: string | null;
  /** El bonus activo del escenario se cumplió → su festejo propio. */
  bonusDone: boolean;
  completedPlanObjectives: readonly number[];
  justPlanObjectiveIdx: number | null;
  allPlanObjectivesDone: boolean;
  // Sub-unidades (fase 17.B)
  subUnit: ActiveSubUnitClient | null;
  subUnitObjectives: readonly string[];
  subUnitReps: readonly number[];
  subUnitCounts: readonly number[];
  subUnitCompleted: readonly number[];
  /** `true` si la sub-unidad activa se está REPASANDO (ya aprobada). */
  subUnitReview: boolean;
  justSubObjIdx: number | null;
  /**
   * Sube con CADA rep nueva (no sólo con un objetivo nuevo): dispara el toast
   * del tracker con el conteo aunque sea el mismo objetivo otra vez.
   */
  subRepTick: number;
}

/**
 * Trackers EN VIVO de objetivos: misión de escenario, objetivos de la unit del
 * Programa y sub-unidades autoradas. Todos se alimentan de window events que el
 * voice-client redispara desde el WS (`orborix:objective.progress`,
 * `orborix:subunit.advanced`) y acumulan de forma OPTIMISTA (marca y no
 * desmarca); el juez del cierre persiste la verdad.
 */
export function useObjectiveTrackers(args: {
  scenarioMission: ScenarioMission | null;
  planObjectives: readonly string[];
}): UseObjectiveTrackersReturn {
  const { scenarioMission, planObjectives } = args;
  const [lessonCompletion, setLessonCompletion] = useState<LessonCompletion | null>(null);
  const completionRef = useRef<LessonCompletion | null>(null);
  useEffect(() => {
    const onCompleted = (ev: Event) => {
      const parsed = lessonCompletionSchema.safeParse((ev as CustomEvent).detail);
      if (!parsed.success) return;
      const part = parsed.data;
      const current = subUnitRef.current;
      if (current && (current.subUnitId !== part.subUnitId || current.unitId !== part.unitId))
        return;
      completionRef.current = part;
      setLessonCompletion(part);
    };
    window.addEventListener('orborix:lesson.completed', onCompleted);
    return () => window.removeEventListener('orborix:lesson.completed', onCompleted);
  }, []);

  // ─── Tracker de misión en vivo (B6) ────────────────────────────────────
  // El backend emite `objective.progress` por turno (piggyback sobre el
  // corrections-runner, sin LLM extra). Sin escenario con misión, no nos
  // suscribimos.
  const [completedObjectives, setCompletedObjectives] = useState<ReadonlyArray<string>>([]);
  const [justObjectiveId, setJustObjectiveId] = useState<string | null>(null);
  const [practicedObjectives, setPracticedObjectives] = useState<ReadonlyArray<string>>([]);
  const [justPracticedId, setJustPracticedId] = useState<string | null>(null);
  // Espejo en ref de los ids ya sonados: el sonido va FUERA del state updater
  // (StrictMode los doble-invoca) y sólo en objetivos nuevos.
  const soundedObjIds = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!scenarioMission) return;
    const onProgress = (ev: Event): void => {
      const detail = (ev as CustomEvent).detail as
        | { objectiveId?: unknown; met?: unknown; assisted?: unknown }
        | undefined;
      if (!detail || typeof detail.objectiveId !== 'string' || detail.met !== true) return;
      // Charla asistida: practicado con la respuesta modelo NO cumple la misión
      // (lo consume también `useAssistedTurn` para ofrecer «Hacelo solo»); el
      // tracker lo muestra como practicado.
      if (detail.assisted === true) {
        const pid = detail.objectiveId;
        // Practicado con ayuda: el mismo sonido corto (maqueta 1 de la asistida, 16/10).
        const key = `practiced:${pid}`;
        if (!soundedObjIds.current.has(key)) {
          soundedObjIds.current.add(key);
          sfx.play('objective-met');
        }
        setPracticedObjectives((prev) => (prev.includes(pid) ? prev : [...prev, pid]));
        setJustPracticedId(pid);
        return;
      }
      const id = detail.objectiveId;
      // Objetivo NUEVO → `objective-met` (el motor lo gatea en realtime). Si
      // esta marca completa TODOS los primarios calla: la misma transición
      // dispara el festejo grande (celebrate-big) y no apilamos sonidos.
      // El bonus tampoco: tiene su festejo propio, con su sonido.
      if (!soundedObjIds.current.has(id)) {
        soundedObjIds.current.add(id);
        const { primary, bonus } = splitObjectives(scenarioMission.objectives);
        const completesAll =
          primary.length > 0 && primary.every((o) => soundedObjIds.current.has(o.id));
        if (!completesAll && id !== bonus?.id) sfx.play('objective-met');
      }
      setCompletedObjectives((prev) => (prev.includes(id) ? prev : [...prev, id]));
      setJustObjectiveId(id);
    };
    window.addEventListener('orborix:objective.progress', onProgress);
    return () => window.removeEventListener('orborix:objective.progress', onProgress);
  }, [scenarioMission]);

  // Tracker en vivo de la UNIDAD (fase 16.B). El extractor juzga los objetivos
  // de la unit piggyback (objectiveId `u-<index>`) y emite el mismo evento
  // `objective.progress`. Sin objetivos de unit, no nos suscribimos.
  const [completedPlanObjectives, setCompletedPlanObjectives] = useState<ReadonlyArray<number>>([]);
  const [justPlanObjectiveIdx, setJustPlanObjectiveIdx] = useState<number | null>(null);
  useEffect(() => {
    if (planObjectives.length === 0) return;
    const onProgress = (ev: Event): void => {
      const detail = (ev as CustomEvent).detail as | { objectiveId?: unknown; met?: unknown } | undefined;
      if (!detail || typeof detail.objectiveId !== 'string' || detail.met !== true) return;
      const m = /^u-(\d+)$/.exec(detail.objectiveId);
      if (!m) return;
      const idx = Number.parseInt(m[1]!, 10);
      if (!Number.isInteger(idx) || idx < 0 || idx >= planObjectives.length) return;
      // Objetivo de unit NUEVO → `objective-met`. Acá el último también suena:
      // no hay festejo de capa para "todos los de la unit" (aparece el FAB de
      // cierre) y el diseño marca ese momento como objective-met.
      if (!soundedObjIds.current.has(detail.objectiveId)) {
        soundedObjIds.current.add(detail.objectiveId);
        sfx.play('objective-met');
      }
      setCompletedPlanObjectives((prev) => (prev.includes(idx) ? prev : [...prev, idx]));
      setJustPlanObjectiveIdx(idx);
    };
    window.addEventListener('orborix:objective.progress', onProgress);
    return () => window.removeEventListener('orborix:objective.progress', onProgress);
  }, [planObjectives]);

  // ¿Demostró TODOS los objetivos de la unit en vivo? Habilita el FAB de cierre
  // (como en escenarios). Ojo: "todos en vivo" ≠ "dominada" — el dominio es
  // espaciado (lo decide el juez del cierre). Es sólo el momento natural de cerrar.
  const allPlanObjectivesDone = useMemo<boolean>(
    () => planObjectives.length > 0 && completedPlanObjectives.length >= planObjectives.length,
    [planObjectives, completedPlanObjectives],
  );

  // ─── Tracker de SUB-UNIDAD en vivo (fase 17.B) ─────────────────────────
  // Programa con sub-unidades autoradas: el server emite `subunit.advanced` al
  // arrancar (la activa = resume point) y en cada avance. El tracker usa estos
  // objetivos (con anillo de reps) en vez de los objetivos planos de la unit.
  // `objective.progress` trae `count/reps` para llenar cada anillo.
  const [subUnit, setSubUnit] = useState<ActiveSubUnitClient | null>(null);
  const [subCounts, setSubCounts] = useState<Record<string, number>>({});
  const [justSubObjId, setJustSubObjId] = useState<string | null>(null);
  // Espejos en ref para decidir el sonido de cada rep dentro de los listeners
  // (los effects no tienen deps; el state de arriba no está en su closure).
  const subUnitRef = useRef<ActiveSubUnitClient | null>(null);
  const subCountsRef = useRef<Record<string, number>>({});
  // Cada rep nueva → toast del tracker con el conteo.
  const [subRepTick, setSubRepTick] = useState(0);

  useEffect(() => {
    const onAdvanced = (ev: Event): void => {
      const d = (ev as CustomEvent).detail as Partial<SubUnitAdvancedDetail> | undefined;
      if (
        !d ||
        typeof d.subUnitId !== 'string' ||
        typeof d.order !== 'number' ||
        typeof d.total !== 'number' ||
        !Array.isArray(d.objectives)
      ) {
        return;
      }
      const objectives = d.objectives
        .filter(
          (o): o is SubUnitAdvancedDetail['objectives'][number] =>
            !!o &&
            typeof o.id === 'string' &&
            typeof o.criterion === 'string' &&
            typeof o.reps === 'number',
        )
        .map((o) => ({
          id: o.id,
          criterion: o.criterion,
          label: typeof o.label?.es === 'string' && o.label.es.length > 0 ? o.label.es : null,
          reps: o.reps,
        }));
      if (objectives.length === 0) return;
      const next: ActiveSubUnitClient = {
        unitId: d.unitId ?? '',
        subUnitId: d.subUnitId,
        order: d.order,
        total: d.total,
        titleEs: typeof d.titleEs === 'string' ? d.titleEs : '',
        titleEn: typeof d.titleEn === 'string' ? d.titleEn : '',
        blurbEs: typeof d.blurbEs === 'string' && d.blurbEs.length > 0 ? d.blurbEs : null,
        objectives,
        review: d.review === true,
      };
      if (completionRef.current && completionRef.current.subUnitId !== next.subUnitId) {
        completionRef.current = null;
        setLessonCompletion(null);
      }
      setSubUnit(next);
      subUnitRef.current = next;
      // Sembrar los contadores desde el payload: al REANUDAR, el server manda el
      // progreso persistido (resume point) → los anillos arrancan en 2/3, no en 0.
      const seededCounts: Record<string, number> = {};
      for (const o of d.objectives) {
        if (o && typeof o.id === 'string' && typeof o.count === 'number' && o.count > 0) {
          seededCounts[o.id] = o.count;
        }
      }
      setSubCounts(seededCounts);
      subCountsRef.current = seededCounts;
      setJustSubObjId(null);
    };
    window.addEventListener('orborix:subunit.advanced', onAdvanced);
    return () => window.removeEventListener('orborix:subunit.advanced', onAdvanced);
  }, []);

  useEffect(() => {
    const onProgress = (ev: Event): void => {
      const detail = (ev as CustomEvent).detail as
        | { objectiveId?: unknown; met?: unknown; count?: unknown }
        | undefined;
      // Modo sub-unidad: el server manda `count` (anillo de reps). Sin `count`
      // es un objetivo binario (escenario/legacy) → lo ignora este listener.
      if (!detail || typeof detail.objectiveId !== 'string' || detail.met !== true) return;
      if (typeof detail.count !== 'number') return;
      const id = detail.objectiveId;
      const count = detail.count;
      // Rep de anillo NUEVA → `streak-up` (momentum; el motor lo gatea en
      // realtime) + toast con el conteo. Si la rep completa la sub-unidad
      // ENTERA calla: ese momento lo cierra el final de clase (reconocimiento
      // del coach + `LessonCompletedCard`), sin festejo aparte.
      const unit = subUnitRef.current;
      const obj = unit?.objectives.find((o) => o.id === id);
      const prevCount = subCountsRef.current[id] ?? 0;
      subCountsRef.current = { ...subCountsRef.current, [id]: count };
      if (unit && obj && count > prevCount) {
        const completesSubUnit =
          count >= obj.reps &&
          unit.objectives.every((o) => (subCountsRef.current[o.id] ?? 0) >= o.reps);
        if (!completesSubUnit) sfx.play('streak-up');
        setSubRepTick((t) => t + 1);
      }
      setSubCounts((prev) => (prev[id] === count ? prev : { ...prev, [id]: count }));
      setJustSubObjId(id);
    };
    window.addEventListener('orborix:objective.progress', onProgress);
    return () => window.removeEventListener('orborix:objective.progress', onProgress);
  }, []);

  // Derivados del tracker de sub-unidad para los componentes de escena.
  // A can-do lesson's objective shows its skill label: its criterion is the
  // judge's instruction, long and in English. An old sub-unit has no label.
  const subUnitObjectives = useMemo<readonly string[]>(
    () => subUnit?.objectives.map((o) => o.label ?? o.criterion) ?? [],
    [subUnit],
  );
  const subUnitReps = useMemo<readonly number[]>(
    () => subUnit?.objectives.map((o) => o.reps) ?? [],
    [subUnit],
  );
  const subUnitCounts = useMemo<readonly number[]>(
    () =>
      subUnit?.objectives.map((o) => Math.min(Math.max(0, subCounts[o.id] ?? 0), o.reps)) ?? [],
    [subUnit, subCounts],
  );
  const subUnitCompleted = useMemo<readonly number[]>(
    () =>
      subUnit
        ? subUnit.objectives
            .map((o, i) => ((subCounts[o.id] ?? 0) >= o.reps ? i : -1))
            .filter((i) => i >= 0)
        : [],
    [subUnit, subCounts],
  );
  const subUnitReview = subUnit?.review === true;
  const justSubObjIdx = useMemo<number | null>(() => {
    if (!subUnit || !justSubObjId) return null;
    const idx = subUnit.objectives.findIndex((o) => o.id === justSubObjId);
    return idx >= 0 ? idx : null;
  }, [subUnit, justSubObjId]);
  // ¿Se cumplieron TODOS los objetivos primarios de la misión? Mismo criterio
  // que el MissionTracker (`allDone`). Lo derivamos acá para que las escenas
  // (mobile/desktop) puedan disparar la celebración en la transición a `true`
  // sin recalcular la lógica de split. El bonus no cuenta para el "completo".
  const allObjectivesDone = useMemo<boolean>(() => {
    if (!scenarioMission) return false;
    const { primary } = splitObjectives(scenarioMission.objectives);
    if (primary.length === 0) return false;
    const done = new Set(completedObjectives);
    return primary.every((o) => done.has(o.id));
  }, [scenarioMission, completedObjectives]);

  const scenarioBonus = scenarioMission ? splitObjectives(scenarioMission.objectives).bonus : null;
  const bonusDone = !!scenarioBonus && completedObjectives.includes(scenarioBonus.id);
  const bonusPending =
    allObjectivesDone && scenarioBonus && !bonusDone && !lessonCompletion ? scenarioBonus.label : null;

  return {
    lessonCompletion,
    completedObjectives,
    justObjectiveId,
    allObjectivesDone,
    practicedObjectives,
    justPracticedId,
    bonusPending,
    bonusDone,
    completedPlanObjectives,
    justPlanObjectiveIdx,
    allPlanObjectivesDone,
    subUnit,
    subUnitObjectives,
    subUnitReps,
    subUnitCounts,
    subUnitCompleted,
    subUnitReview,
    justSubObjIdx,
    subRepTick,
  };
}
